import { useTranslation } from 'react-i18next';

import type {
  SchedulePlacementState,
  ScheduleRunStatus,
  ScheduleStatus,
} from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { cn } from '@nucleus-commerce/ui/lib/utils';

const SUCCESS = 'bg-success/15 text-success border-transparent dark:bg-success/20';
const NEUTRAL = 'border-border text-muted-foreground';
const INFO = 'bg-primary/10 text-foreground border-transparent';
const WARNING = 'bg-warning/20 text-warning-foreground border-transparent dark:bg-warning/25';
const DANGER = 'bg-destructive/10 text-destructive border-transparent';

const STATUS: Record<ScheduleStatus, string> = {
  draft: NEUTRAL,
  active: SUCCESS,
  paused: WARNING,
  ended: NEUTRAL,
};

const RUN_STATUS: Record<ScheduleRunStatus, string> = {
  queued: INFO,
  running: INFO,
  done: SUCCESS,
  failed: DANGER,
};

const PLACEMENT: Record<SchedulePlacementState, string> = {
  scheduled: INFO,
  live: SUCCESS,
  offline: NEUTRAL,
};

export function ScheduleStatusBadge({
  status,
  className,
}: {
  status: ScheduleStatus;
  className?: string;
}) {
  const { t } = useTranslation('schedules');
  return (
    <Badge variant="outline" className={cn(STATUS[status], className)}>
      {t(`status.${status}`)}
    </Badge>
  );
}

export function RunStatusBadge({ status }: { status: ScheduleRunStatus }) {
  const { t } = useTranslation('schedules');
  return (
    <Badge variant="outline" className={RUN_STATUS[status]}>
      {t(`runStatus.${status}`)}
    </Badge>
  );
}

export function PlacementBadge({
  state,
  label,
}: {
  state: SchedulePlacementState;
  /** Prefix, e.g. the store's name. */
  label?: string;
}) {
  const { t } = useTranslation('schedules');
  return (
    <Badge variant="outline" className={PLACEMENT[state]}>
      {label ? `${label}: ` : null}
      {t(`placement.${state}`)}
    </Badge>
  );
}
